import { motion } from "framer-motion";
import {
    Cpu,
    Radio,
    ScanBarcode,
    Scale,
    Eye,
    Bot,
    ArrowRight,
    ArrowUpRight,
} from "lucide-react";

const SC_IMG =
    "https://images.pexels.com/photos/7451917/pexels-photo-7451917.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PRODUCTS = [
    {
        name: "Caddy",
        sku: "RT-CB01",
        status: "In Testing",
        statusTone: "live",
        bullets: [
            "RFID scanner",
            "Real-time tally",
            "Cashless UPI / Card payments",
            "Self-checkout",
            "Geofencing / Asset immobilisation",
            "Data lake",
        ],
        bestFor: "Pilot stores · grocery · convenience",
        icon: Radio,
    },
    {
        name: "Caddy Pro",
        sku: "RT-SC01",
        status: "In Development",
        statusTone: "dev",
        bullets: [
            "All Caddy features",
            "Barcode scanning",
            "Load-cell weighing",
            "Theft protection",
        ],
        bestFor: "Supermarkets · fresh formats",
        icon: ScanBarcode,
    },
    {
        name: "Caddy Vision",
        sku: "RT-SC02",
        status: "Planned",
        statusTone: "planned",
        bullets: [
            "All Caddy Pro features",
            "AI vision scanner — no scanning required",
        ],
        bestFor: "Hypermarkets · high-throughput",
        icon: Eye,
    },
    {
        name: "Cobot",
        sku: "RT-SC03",
        status: "Planned",
        statusTone: "planned",
        bullets: [
            "Motorised walk-along robot",
            "Generative AI shopping co-pilot",
            "Personalised user experience",
            "Synchronised retail integration (LLM, SLAM)",
        ],
        bestFor: "Flagship stores · premium experience",
        icon: Bot,
    },
];

const PILLS = [
    { icon: Eye, label: "AI vision (Vision tier)" },
    { icon: ScanBarcode, label: "RFID + barcode + scale" },
    { icon: Cpu, label: "On-device inference" },
    { icon: Radio, label: "Online payments (UPI / Card)" },
    { icon: Bot, label: "Generative AI co-pilot (Cobot)" },
];

function StatusBadge({ status, tone }) {
    const styles = {
        live: "border-rt-teal/40 bg-rt-teal/10 text-rt-teal",
        dev: "border-amber-400/40 bg-amber-400/10 text-amber-300",
        planned: "border-white/15 bg-white/5 text-white/60",
    };
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] font-mono uppercase tracking-[0.18em] ${styles[tone]}`}
        >
            <span className={`w-1.5 h-1.5 rounded-full ${tone === "live" ? "bg-rt-teal animate-pulse" : tone === "dev" ? "bg-amber-300" : "bg-white/40"}`} />
            {status}
        </span>
    );
}

export default function SmartCaddy() {
    return (
        <section
            id="smartcaddy"
            data-testid="smartcaddy-section"
            className="relative bg-rt-surface border-y border-white/10 py-24 lg:py-36 overflow-hidden"
        >
            <div className="absolute inset-0 rt-grid-bg opacity-30 pointer-events-none" />

            <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-12 items-start mb-14">
                    {/* Left: image + brand block */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.6 }}
                        className="lg:col-span-5 relative"
                    >
                        <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                            <img
                                src={SC_IMG}
                                alt="SmartCaddy carts in store"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-rt-surface via-rt-surface/30 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-8">
                                <div className="rt-mono-label mb-2">A ReTechler product line</div>
                                <div className="font-heading text-4xl lg:text-5xl text-white tracking-tight">
                                    Smart<span className="text-rt-teal">Caddy</span>
                                </div>
                                <div className="text-rt-text2 text-sm mt-2">
                                    smartcaddy.store
                                </div>
                            </div>
                            <div className="absolute top-4 right-4 rt-mono-label !text-[10px] bg-black/60 backdrop-blur px-2 py-1 border border-white/10">
                                4 PRODUCTS · 1 PLATFORM
                            </div>
                        </div>
                    </motion.div>

                    {/* Right: header text */}
                    <div className="lg:col-span-7 flex flex-col">
                        <div className="rt-eyebrow mb-4">Flagship product line / SmartCaddy</div>
                        <h2 className="rt-h2 text-4xl md:text-5xl lg:text-6xl text-white">
                            A cart that<br />
                            <span className="italic font-light text-rt-teal">sees, weighs, charges</span>
                            <br />
                            and sells.
                        </h2>
                        <p className="mt-6 text-rt-text2 text-base lg:text-lg max-w-2xl leading-relaxed">
                            A full-stack smart retail platform — hardware + AI + SaaS — packaged as
                            four progressively capable products. Caddy is currently in test;
                            Caddy Pro, Caddy Vision and Cobot are on the active product roadmap.
                        </p>

                        {/* Capability pills */}
                        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {PILLS.map((p, i) => {
                                const Icon = p.icon;
                                return (
                                    <div
                                        key={i}
                                        className="flex items-center gap-2.5 border border-white/10 bg-rt-bg/60 px-3.5 py-2.5"
                                    >
                                        <Icon className="w-4 h-4 text-rt-teal shrink-0" />
                                        <span className="text-xs text-white/85 font-mono tracking-tight">
                                            {p.label}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href="https://smartcaddy.store/"
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="smartcaddy-cta-store"
                                className="inline-flex items-center gap-2 bg-rt-teal text-black font-medium px-5 py-3 hover:bg-rt-tealDim transition-colors"
                            >
                                Visit smartcaddy.store <ArrowUpRight className="w-4 h-4" />
                            </a>
                            <a
                                href="#compare"
                                data-testid="smartcaddy-cta-compare"
                                className="inline-flex items-center gap-2 border border-white/20 text-white px-5 py-3 hover:bg-white/5 transition-colors"
                            >
                                Compare the four products <ArrowRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Product line grid — Caddy / Caddy Pro / Caddy Vision / Cobot */}
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
                    {PRODUCTS.map((p, i) => {
                        const Icon = p.icon;
                        return (
                            <div
                                key={i}
                                data-testid={`smartcaddy-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                                className="bg-rt-surface p-6 lg:p-7 flex flex-col"
                            >
                                <div className="flex items-start justify-between mb-6">
                                    <div className="w-10 h-10 flex items-center justify-center border border-white/15 text-white/85">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <StatusBadge status={p.status} tone={p.statusTone} />
                                </div>
                                <div className="font-heading text-2xl text-white tracking-tight">
                                    {p.name}
                                </div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-rt-text3 font-mono mt-1">
                                    {p.sku}
                                </div>
                                <ul className="mt-5 space-y-2 text-sm text-rt-text2 flex-1">
                                    {p.bullets.map((b, j) => (
                                        <li key={j} className="flex gap-2">
                                            <span className="text-rt-teal shrink-0">·</span>
                                            <span>{b}</span>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-5 pt-4 border-t border-white/10 text-xs text-rt-text3">
                                    {p.bestFor}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
